import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import AddressCard from '../components/AddressCard';
import AddressForm from '../components/AddressForm';
import toast from 'react-hot-toast';
import './AccountPage.css';

const STATUS_COLORS = { processing:'#f5a623', packed:'#4a90d9', shipped:'#7ed321', out_for_delivery:'#9013fe', delivered:'#52b788', cancelled:'#d0021b' };

const AccountPage = () => {
  const { dbUser, currentUser, logout } = useAuth();
  const [orders, setOrders] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('orders');
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [ordersRes, addressesRes] = await Promise.all([
          api.get('/orders/my'),
          api.get('/addresses')
        ]);
        setOrders(ordersRes.data.orders);
        setAddresses(addressesRes.data.addresses);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetchData();
  }, []);

  const handleAddressSubmit = async (formData) => {
    try {
      if (editingAddress) {
        await api.put(`/addresses/${editingAddress._id}`, formData);
        toast.success('Address updated successfully');
      } else {
        await api.post('/addresses', formData);
        toast.success('Address added successfully');
      }
      setShowAddressForm(false);
      setEditingAddress(null);
      
      // Refresh addresses
      const res = await api.get('/addresses');
      setAddresses(res.data.addresses);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save address');
    }
  };

  const handleDeleteAddress = async (id) => {
    if (window.confirm('Are you sure you want to delete this address?')) {
      try {
        await api.delete(`/addresses/${id}`);
        toast.success('Address deleted');
        const res = await api.get('/addresses');
        setAddresses(res.data.addresses);
      } catch (err) {
        toast.error('Failed to delete address');
      }
    }
  };

  const handleSetDefaultAddress = async (id) => {
    try {
      await api.patch(`/addresses/${id}/default`);
      toast.success('Default address updated');
      const res = await api.get('/addresses');
      setAddresses(res.data.addresses);
    } catch (err) {
      toast.error('Failed to update default address');
    }
  };

  return (
    <div className="account-page">
      <div className="page-header"><h1>My Account</h1></div>
      <div className="container">
        <div className="account-layout">
          <aside className="account-sidebar">
            <div className="account-avatar">
              {currentUser?.photoURL
                ? <img src={currentUser.photoURL} alt="avatar" />
                : <div className="avatar-placeholder">{(dbUser?.name || 'U')[0]}</div>
              }
            </div>
            <h3>{dbUser?.name || 'Customer'}</h3>
            <p>{dbUser?.email}</p>
            <nav className="account-nav">
              <button className={tab === 'orders' ? 'active' : ''} onClick={() => setTab('orders')}>My Orders</button>
              <button className={tab === 'addresses' ? 'active' : ''} onClick={() => setTab('addresses')}>My Addresses</button>
              <button className={tab === 'profile' ? 'active' : ''} onClick={() => setTab('profile')}>Profile</button>
              <button onClick={logout} className="logout-btn">Logout</button>
            </nav>
          </aside>

          <div className="account-content">
            {tab === 'orders' && (
              <div>
                <h2>Order History</h2>
                {loading ? <div className="loading-spinner"><div className="spinner" /></div>
                  : orders.length === 0 ? (
                    <div className="empty-state">
                      <h3>No orders yet</h3>
                      <p>Start shopping to see your orders here</p>
                      <Link to="/shop" className="btn btn-primary" style={{marginTop:'20px'}}>Shop Now</Link>
                    </div>
                  ) : orders.map(order => (
                    <div key={order._id} className="order-card">
                      <div className="order-card__header">
                        <div>
                          <p className="order-id">Order #{order.orderId}</p>
                          <p className="order-date">{new Date(order.createdAt).toLocaleDateString('en-IN', {day:'numeric',month:'short',year:'numeric'})}</p>
                        </div>
                        <div style={{textAlign:'right'}}>
                          <span className="order-status" style={{color: STATUS_COLORS[order.deliveryStatus] || '#999'}}>
                            ● {order.deliveryStatus?.replace(/_/g,' ')}
                          </span>
                          <p className="order-total">₹{order.totalAmount?.toFixed(0)}</p>
                        </div>
                      </div>
                      <div className="order-items-preview">
                        {order.items?.slice(0,3).map((item,i) => (
                          <img key={i} src={item.image || 'https://via.placeholder.com/50'} alt={item.name} title={item.name} />
                        ))}
                        {order.items?.length > 3 && <span>+{order.items.length - 3} more</span>}
                      </div>
                      <div className="order-card__footer">
                        <Link to={`/track/${order.orderId}`} className="btn btn-outline btn-sm">Track Order</Link>
                      </div>
                    </div>
                  ))}
              </div>
            )}
            {tab === 'profile' && (
              <div>
                <h2>Profile Details</h2>
                <div className="profile-info">
                  <div className="info-row"><span>Name</span><strong>{dbUser?.name}</strong></div>
                  <div className="info-row"><span>Email</span><strong>{dbUser?.email}</strong></div>
                  <div className="info-row"><span>Phone</span><strong>{dbUser?.phone || 'Not added'}</strong></div>
                  <div className="info-row"><span>Account Type</span><strong style={{textTransform:'capitalize'}}>{dbUser?.role}</strong></div>
                </div>
              </div>
            )}
            {tab === 'addresses' && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  <h2>My Addresses</h2>
                  <button className="btn btn-primary btn-sm" onClick={() => { setEditingAddress(null); setShowAddressForm(true); }}>
                    + Add New Address
                  </button>
                </div>
                {addresses.length === 0 ? (
                  <div className="empty-state">
                    <h3>No addresses saved</h3>
                    <p>Add a delivery address for faster checkout.</p>
                  </div>
                ) : (
                  <div className="addresses-grid">
                    {addresses.map(addr => (
                      <AddressCard 
                        key={addr._id} 
                        address={addr} 
                        onEdit={(address) => { setEditingAddress(address); setShowAddressForm(true); }}
                        onDelete={handleDeleteAddress}
                        onSetDefault={handleSetDefaultAddress}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      
      {showAddressForm && (
        <AddressForm 
          initialData={editingAddress} 
          onSubmit={handleAddressSubmit} 
          onCancel={() => { setShowAddressForm(false); setEditingAddress(null); }} 
        />
      )}
    </div>
  );
};

export default AccountPage;
